import { useFetcher } from "@remix-run/react";
import { useCallback, useEffect, useState } from "react";
import ReCaptcha from "react-google-recaptcha";
import { RiAddLine } from "react-icons/ri";
import { ClientOnly } from "remix-utils/client-only";
import { AuthenticityTokenInput } from "remix-utils/csrf/react";
import { ValidatedForm } from "remix-validated-form";

import { Modal } from "~/components/Modal";
import { Button } from "~/components/ui/button";
import { ValidatedInput } from "~/components/ui/forms/input";
import { useManagedFetcher } from "~/hooks/use-managed-fetcher";
import { downloadEmailValidator } from "~/validators/download-email";

export function AskEmailModal({
  productId,
  downloadUrl,
  isOpen,
  onClose,
}: {
  productId: number;
  downloadUrl: string;
  isOpen: boolean;
  onClose: (val: boolean) => void;
}) {
  const [wasSubmitted, setWasSubmitted] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState("");

  const fetcher = useFetcher<any>();

  const [isFetcherLoading, isFetcherDone] = useManagedFetcher({
    fetcher,
    toastSuccessMessage: fetcher.data?.successMessage || "",
  });

  const closeFn = useCallback(
    (val: boolean) => {
      setWasSubmitted(false);
      onClose(val);
    },
    [onClose]
  );

  useEffect(() => {
    if (isFetcherDone && wasSubmitted) {
      closeFn(false);
    }
  }, [isFetcherDone, closeFn, wasSubmitted]);

  useEffect(() => {
    if (isFetcherLoading) {
      setWasSubmitted(true);
    }
  }, [isFetcherLoading]);

  useEffect(() => {
    if (isFetcherDone && wasSubmitted && window) {
      window.open(downloadUrl, "_blank");
    }
  }, [isFetcherDone, downloadUrl, wasSubmitted]);

  return (
    <Modal isOpen={isOpen} onClose={() => onClose(false)} title="Télécharger un document" size="md">
      <ValidatedForm
        validator={downloadEmailValidator}
        fetcher={fetcher}
        method="POST"
        action="/product"
        className="flex flex-col gap-6"
      >
        <input type="hidden" name="_action" value="sendEmailForDownload" />
        <AuthenticityTokenInput />

        <p className="text-center text-sm leading-6 text-grey-600">
          Afin de télécharger ces documents, merci de bien vouloir renseigner votre adresse email
          afin que nous puissions vous recontacter dans les prochains jours et nous assurer que vous
          avez bien trouvé les produits en adéquation avec votre recherche.
        </p>
        <ValidatedInput
          name="productId"
          id="productId"
          type="hidden"
          value={productId.toString()}
        />
        <ValidatedInput
          label="Email"
          name="email"
          id="email"
          errorMessage={fetcher.data?.fieldErrors?.email}
        />
        <ClientOnly fallback={null}>
          {() => (
            <ReCaptcha
              sitekey={window?.ENV?.RECAPTCHA_SITE_KEY ?? ""}
              onChange={(token) => setRecaptchaToken(token || "")}
            />
          )}
        </ClientOnly>
        <ValidatedInput type="hidden" id="recaptcha" name="recaptcha" value={recaptchaToken} />
        <Button
          type="submit"
          icon={<RiAddLine />}
          loadingState={isFetcherLoading ? "loading" : "idle"}
        >
          Télécharger
        </Button>
      </ValidatedForm>
    </Modal>
  );
}
